
import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Edit3, Save, X } from 'lucide-react';
import { useUpdateJob } from '@/hooks/mutations/useUpdateJob';
import { toast } from 'sonner';

interface EditableJobRequirementsProps {
  job: Job;
  canEdit: boolean;
}

export const EditableJobRequirements: React.FC<EditableJobRequirementsProps> = ({ job, canEdit }) => {
  const { mutate: updateJob, isPending: isUpdating } = useUpdateJob();
  const [isEditing, setIsEditing] = useState(false);
  const [editRequirements, setEditRequirements] = useState(job.requirements || '');

  const handleSave = async () => {
    updateJob({ id: job.id, data: { requirements: editRequirements } }, {
      onSuccess: () => {
        toast.success('Job requirements updated successfully');
        setIsEditing(false);
      }
    });
  };

  const handleCancel = () => {
    setEditRequirements(job.requirements || '');
    setIsEditing(false);
  };

  if (!job.requirements && !canEdit) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Requirements</CardTitle>
          {canEdit && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <Textarea
              value={editRequirements}
              onChange={(e) => setEditRequirements(e.target.value)}
              placeholder="Enter job requirements..."
              className="min-h-[150px]"
            />
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isUpdating}>
                <Save className="h-4 w-4 mr-2" />
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isUpdating}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="whitespace-pre-wrap text-sm">
            {job.requirements || (canEdit && (
              <div className="text-muted-foreground italic">
                No requirements specified. Click Edit to add them.
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
